import type { ReactNode } from 'react';
import { X } from '../../../icons';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import './Modal.css';

/**
 * 공용 모달 셸 — all-frame.html #2c·#2d·#2e 의 "레이아웃 위 중앙 모달" 표준.
 *
 * 기존 모달(EventCreateModal·TaskCreateModal·ComposeModal …)은 각자 scrim/헤더/닫기
 * 버튼/포커스 트랩을 손으로 짜서 같은 코드가 10벌 넘게 흩어져 있다. 그 파일들은
 * 건드리지 않고(회귀 금지), **신규 모달만** 본 셸을 써서 더 이상 늘지 않게 한다.
 *
 * 포커스 트랩·ESC 는 기존 훅(useFocusTrap/useEscapeClose)을 그대로 조합한다 — 재구현 없음.
 *
 * body 는 기본으로 세로 스택 + 패딩을 준다. 2단 구성(좌 내비 + 우 상세)처럼 직접
 * 레이아웃을 잡아야 하면 `bodyFlat` 로 패딩·스택을 끈다.
 */

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  /** 헤더 타이틀. */
  title: ReactNode;
  onClose: () => void;
  /** sm=620 / md=720 / lg=840. 기본 md. */
  size?: ModalSize;
  /** 타이틀 앞 강조 바 (신규 생성 계열 모달). */
  accent?: boolean;
  /** body 의 기본 패딩·세로스택 해제 — 직접 레이아웃 잡을 때. */
  bodyFlat?: boolean;
  /** 하단 바. 좌측 노트 + 우측 액션은 `ModalFootNote`/`ModalActions` 사용. */
  footer?: ReactNode;
  /** useModalLeave 연동 — 닫힘 트랜지션 단계. */
  isLeaving?: boolean;
  children: ReactNode;
}

export function Modal({
  title, onClose, size = 'md', accent = false, bodyFlat = false, footer, isLeaving = false, children,
}: ModalProps) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  useEscapeClose(onClose);

  return (
    <div className={`wc-modal-scrim${isLeaving ? ' wc-modal-scrim--leaving' : ''}`} onMouseDown={onClose}>
      <div
        ref={trapRef}
        className={`wc-modal wc-modal--${size}${isLeaving ? ' wc-modal--leaving' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="wc-modal__head">
          {accent && <span className="wc-modal__accent" aria-hidden />}
          <span className="wc-modal__title">{title}</span>
          <button type="button" className="wc-modal__x" onClick={onClose} aria-label="닫기">
            <X size={16} aria-hidden />
          </button>
        </header>
        <div className={`wc-modal__body scrollbar-thin${bodyFlat ? ' wc-modal__body--flat' : ''}`}>
          {children}
        </div>
        {footer != null && <footer className="wc-modal__foot">{footer}</footer>}
      </div>
    </div>
  );
}

/** 하단 바 좌측 요약 문구 ("신청 일수 1.5일", "위젯 3개 선택됨"). */
export function ModalFootNote({ children }: { children: ReactNode }) {
  return <span className="wc-modal__note">{children}</span>;
}

/** 하단 바 우측 액션 묶음 — 자동으로 오른쪽 정렬. */
export function ModalActions({ children }: { children: ReactNode }) {
  return <div className="wc-modal__actions">{children}</div>;
}
